import { useMemo } from 'react';
import { useFetchEditableGroupsQuery } from '../../groups/groupsApi';
import { useFetchGroupByIdQuery } from '../../groups/groupsApi';
import { useFetchCoursesByGroupIdQuery } from '../../courses/coursesApi';
import { useGetUsersByGroupIdQuery } from '../../users/usersApi';
import { useGetCourseByIdQuery } from '../../course/courseApi';
import { useGetClassroomEventsQuery } from '../classroomApi';
import { DEMO_CLASSROOMS, DEMO_CLASSROOM_DETAILS, isDemoClassroom } from '../data/mockClassroomData';

export const useClassrooms = (options = {}) => {
  const { data, isLoading, isError, error, refetch } = useFetchEditableGroupsQuery(
    { disableCounts: false },
    options
  );

  // Merge real groups with demo classrooms so the feature always has visible content
  const classrooms = useMemo(() => {
    const realGroups = data ?? [];
    return [...DEMO_CLASSROOMS, ...realGroups];
  }, [data]);

  return {
    classrooms,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useClassroom = (groupId, options = {}) => {
  const isDemo = isDemoClassroom(groupId);

  // For demo classrooms, skip all real API calls
  const { data: group, isLoading: groupLoading } = useFetchGroupByIdQuery(
    { id: groupId, withCounts: true },
    { skip: !groupId || isDemo, ...options }
  );

  const { data: courses, isLoading: coursesLoading } = useFetchCoursesByGroupIdQuery(
    { circleId: groupId },
    { skip: !groupId || isDemo, ...options }
  );

  const { data: members, isLoading: membersLoading } = useGetUsersByGroupIdQuery(
    { circleId: groupId },
    { skip: !groupId || isDemo, ...options }
  );

  const courseId = courses?.[0]?.id;
  const { data: courseDetail, isLoading: courseLoading } = useGetCourseByIdQuery(
    { courseId },
    { skip: !courseId || isDemo, ...options }
  );

  // Return demo data if this is a demo classroom
  if (isDemo) {
    const demoData = DEMO_CLASSROOM_DETAILS[groupId];
    return {
      group: demoData?.group,
      courses: demoData?.courses ?? [],
      courseId: demoData?.courseId,
      courseDetail: demoData?.courseDetail,
      members: demoData?.members ?? [],
      sections: demoData?.sections ?? [],
      isLoading: false,
      isDemo: true,
    };
  }

  return {
    group,
    courses: courses ?? [],
    courseId,
    courseDetail,
    members: members ?? [],
    sections: courseDetail?.toc ?? [],
    isLoading: groupLoading || coursesLoading || membersLoading || courseLoading,
    isDemo: false,
  };
};

export const useClassroomEvents = (groupId, options = {}) => {
  const { data, isLoading } = useGetClassroomEventsQuery({ groupId }, options);
  return {
    events: data ?? [],
    isLoading,
  };
};
